/**
 * useExecuteNodeAction
 *
 * Wraps `client.ts:executeNode` for canvas Generate buttons. Provides:
 *   1. A 1s cooldown per nodeId (via useClickLock) so a fast double-tap
 *      can't fire /execute-node twice. Holds the lock while the request
 *      is in flight PLUS 1s of post-call cooldown so the YDoc-driven
 *      `idle → running` status flip has time to disable the button via
 *      the existing `isRunning` guard.
 *   2. InsufficientSparksError catch — opens the workspace-level
 *      <InsufficientSparksModal> via InsufficientSparksContext instead
 *      of letting the error bubble to console.error.
 *   3. `cancelExecution()` — cancel a running node via the backend.
 *
 * Returns:
 *   - `isLocked` — true while the cooldown window is active for this
 *     node. Buttons should `disabled={isRunning || isLocked}`.
 *   - `runExecute(retryCount)` — fire /execute-node with cooldown
 *     + insufficient-sparks handling.
 *   - `cancelExecution()` — cancel a running node execution.
 *   - `isCancelling` — true while the cancel request is in flight.
 */

import { useCallback, useState } from 'react'
import { executeNode, cancelNodeExecution, InsufficientSparksError } from '../api/client'
import { useShowInsufficientSparks } from '../contexts/InsufficientSparksContext'
import { useActiveCanvasId } from '../contexts/ActiveCanvasIdContext'
import { useClickLock } from './useClickLock'
import type { ApiValue } from '../api/responseDecoders'
import type { ExecuteNodeAnalytics } from '../analytics/nodeAnalytics'
import { trackGenerationCancelled } from '../analytics/interactionAnalytics'

interface UseExecuteNodeActionOptions {
  projectId: string | undefined
  nodeId: string
  /** Status from YDoc — block firing while already running/queued. */
  isRunning: boolean
  cooldownMs?: number
  /** Optional action override (e.g. 'run_speech_to_text'). Omit for default 'generate'. */
  action?: string
  /** Node type, used only to classify the insufficient-credits funnel event
   *  (#3503). Omitting it yields `blocked_action: 'other'`, not a guess. */
  nodeType?: string
}

export interface RunExecuteOptions {
  /**
   * Declared `in_*` fields to send as per-run arguments.
   *
   * The backend persists them before execution and also freezes the admitted
   * request, so a click uses the exact values sent with that request.
   */
  inputUpdates?: Record<string, ApiValue>
  /**
   * Declared `state` fields to send as per-run arguments.
   *
   * `execute-node` freezes these into a detached override that execution
   * consumes, so they are transient action arguments rather than durable node
   * data — see the freeze in `routes/agentic/execute.py`. Used by the Refine
   * tab to hand a run the path of a freshly baked annotation.
   */
  stateUpdates?: Record<string, string>
  /** RFC-1577 `node_generation_submitted` context (tab, mode, …). */
  analytics?: ExecuteNodeAnalytics
}

export interface UseExecuteNodeActionResult {
  isLocked: boolean
  runExecute: (retryCount?: number, options?: RunExecuteOptions) => void
  cancelExecution: () => void
  isCancelling: boolean
}

export function useExecuteNodeAction({
  projectId,
  nodeId,
  isRunning,
  cooldownMs = 1000,
  action,
  nodeType,
}: UseExecuteNodeActionOptions): UseExecuteNodeActionResult {
  const { isLocked: isLockedKey, run } = useClickLock(cooldownMs)
  const showInsufficientSparks = useShowInsufficientSparks()
  const canvasId = useActiveCanvasId()
  const [isCancelling, setIsCancelling] = useState(false)

  const runExecute = useCallback(
    (retryCount: number = 1, options?: RunExecuteOptions) => {
      if (isRunning || !projectId) return
      const inputUpdates = options?.inputUpdates
      const stateUpdates = options?.stateUpdates
      const analytics: ExecuteNodeAnalytics = { ...options?.analytics, ...(nodeType ? { nodeType } : {}) }
      void run(nodeId, async () => {
        try {
          await executeNode(projectId, nodeId, {
            action,
            count: retryCount,
            canvasId,
            ...(inputUpdates ? { inputUpdates } : {}),
            ...(stateUpdates ? { stateUpdates } : {}),
            analytics,
          })
        } catch (err) {
          if (err instanceof InsufficientSparksError) {
            showInsufficientSparks({
              balance: err.balance,
              required: err.required,
              nodeId,
              nodeType,
              projectId,
            })
            return
          }
          // eslint-disable-next-line no-console
          console.error('executeNode failed:', err)
        }
      })
    },
    [isRunning, projectId, nodeId, nodeType, action, canvasId, run, showInsufficientSparks],
  )

  const cancelExecution = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    trackGenerationCancelled({ projectId, nodeId, nodeType, stage: 'running' })
    cancelNodeExecution(projectId, nodeId, canvasId)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => {
        setIsCancelling(false)
      })
  }, [isRunning, projectId, nodeId, canvasId, isCancelling, nodeType])

  return {
    isLocked: isLockedKey(nodeId),
    runExecute,
    cancelExecution,
    isCancelling,
  }
}
